import React, { useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { RxCross2 } from "react-icons/rx";
import { IoNotificationsOutline } from "react-icons/io5";
import toast from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import { useTranslateText } from "../../../hooks/useTranslateText";

const NOTIFICATION_PROMPT_KEY = "notification_prompt_answered";

const EnableNotificationsModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const ViewManager = useAppSelector((state) => state.ViewManager);
  const userData = useAppSelector((state) => state.userData);
  const translate = useTranslateText();
  const [isRequesting, setIsRequesting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleCloseModal = () => {
    setErrorMsg(null);
    dispatch(
      updateViewState({
        show_enable_notifications_modal: false,
      })
    );
  };

  const handleEnable = async () => {
    if (!("Notification" in window)) {
      toast.error("Браузер не поддерживает уведомления");
      return;
    }
    setIsRequesting(true);
    setErrorMsg(null);
    try {
      const current = Notification.permission;
      if (current === "denied") {
        setErrorMsg("Уведомления заблокированы. Разрешите в настройках браузера для этого сайта.");
        return;
      }

      // OneSignal заранее — чтобы после native сразу вызвать requestPermission
      const OneSignal = await getOneSignal();

      let granted = false;
      if (current === "default") {
        // 1. Нативный запрос — показывает диалог браузера
        const perm = await Notification.requestPermission();
        granted = perm === "granted";
      } else {
        granted = true;
      }

      if (granted) {
        localStorage.setItem(NOTIFICATION_PROMPT_KEY, "granted");
        handleCloseModal();
        // OneSignal в фоне — не блокируем модалку (requestPermission может зависать)
        if (OneSignal?.Notifications?.requestPermission) {
          Promise.race([
            OneSignal.Notifications.requestPermission(),
            new Promise((r) => setTimeout(r, 5000)),
          ]).then(() => {
            if (OneSignal?.login && userData?.user_id) {
              OneSignal.login(`user_${userData.user_id}`).catch(() => {});
            }
          });
        } else if (OneSignal?.login && userData?.user_id) {
          OneSignal.login(`user_${userData.user_id}`).catch(() => {});
        }
      } else {
        localStorage.setItem(NOTIFICATION_PROMPT_KEY, "declined");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[EnableNotifications]", msg, err);
      setErrorMsg(msg);
      toast.error(`Ошибка: ${msg}`);
    } finally {
      setIsRequesting(false);
    }
  };

  const getOneSignal = (): Promise<any> => {
    const OS = (window as any).OneSignal;
    if (OS) return Promise.resolve(OS);
    (window as any).OneSignalDeferred = (window as any).OneSignalDeferred || [];
    return new Promise((resolve) => {
      const t = setTimeout(() => resolve(null), 5000);
      (window as any).OneSignalDeferred.push((O: any) => {
        clearTimeout(t);
        resolve(O);
      });
    });
  };

  const handleDecline = () => {
    localStorage.setItem(NOTIFICATION_PROMPT_KEY, "declined");
    handleCloseModal();
  };

  return (
    <Dialog
      open={ViewManager.show_enable_notifications_modal}
      onClose={handleDecline}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/35 backdrop-blur-sm">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative max-h-[80vh] w-full max-w-sm overflow-hidden rounded-xl bg-primary transition-transform duration-300 ease-out">
            <DialogTitle
              as="h3"
              style={{ backgroundSize: "100%" }}
              className="w-full space-y-5 primary-gradient bg-no-repeat px-4 xl:space-y-7"
            >
              <div className="flex items-center justify-between py-7 font-semibold">
                <span>{translate("Notifications")}</span>
                <RxCross2
                  onClick={handleDecline}
                  className="cursor-pointer text-2xl"
                />
              </div>
            </DialogTitle>

            <div className="relative mx-4 mt-2 flex flex-col items-center pb-8">
              <div className="my-6 flex justify-center">
                <IoNotificationsOutline className="text-6xl text-themeColor" />
              </div>
              <p className="mb-6 text-center text-sm text-text-muted">
                {translate(
                  "Don't miss a message — enable notifications to stay in the loop"
                )}
              </p>
              {errorMsg && (
                <p className="mb-3 w-full rounded-lg bg-red-500/15 px-3 py-2 text-xs text-red-500">
                  {errorMsg}
                </p>
              )}
              <div className="flex w-full gap-3">
                <button
                  onClick={handleDecline}
                  className="flex-1 rounded-lg border border-borderColor py-2.5 text-sm font-medium"
                >
                  {translate("Not now")}
                </button>
                <button
                  onClick={handleEnable}
                  disabled={isRequesting}
                  className="primary-gradient flex-1 rounded-lg py-2.5 text-sm font-medium disabled:opacity-50"
                >
                  {isRequesting ? "..." : translate("Enable")}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default EnableNotificationsModal;
export { NOTIFICATION_PROMPT_KEY };
